import { ownerIrpfStatus, type IrpfConfig } from '@/utils/irpfPolicy';
import { roundByCurrency } from '@/utils/currencyUtils';
import { parseLocalDateKey, toLocalDateKey } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { Contrato, Cliente, Inmueble, TipoContrato, TipoCliente, InmuebleEstado, ContratoPeriodo, Garantia } from '@/types';
import contractService from '@/services/contractService';
import inmuebleService from '@/services/inmuebleService';
import configService from '@/services/configService';
import Button from '@/components/common/Button';
import ClientSearchCard from '@/components/common/ClientSearchCard';
import InmuebleSearchCard from '@/components/common/InmuebleSearchCard';
import GarantiaSelector from '@/components/GarantiaSelector';
import MainLayout from '@/components/layout/MainLayout';

const formatDateForInput = (date: Date): string => toLocalDateKey(date);

const addYears = (date: Date, years: number): Date => {
  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() + years);
  return newDate;
};

const addDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

const buildInitialPeriod = (start: Date): ContratoPeriodo => ({
  fecha_inicio: formatDateForInput(start),
  fecha_fin: formatDateForInput(addYears(start, 1)),
  orden: 1,
  es_prorroga: false,
});

export default function ContractForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const today = new Date();
  const initialPeriod = buildInitialPeriod(today);

  const [form, setForm] = useState<Partial<Contrato>>({
    tipo_contrato: TipoContrato.AUTOMATICO,
    auto_renovacion: true,
    dia_pago: 1,
    comision: 0,
    iva: 0,
    moneda_inquilino: 'UYU',
    moneda_propietario: 'UYU',
    forma_pago: 'MES_VENCIDO',
    tipo_reajuste: 'TOTAL',
    tipo_alquiler: 'ASEGURADO',
    estado: 'ACTIVO',
    fecha_inicio: initialPeriod.fecha_inicio,
    fecha_fin: initialPeriod.fecha_fin,
    fecha_reajuste: formatDateForInput(addYears(today, 1)),
    periodos: [initialPeriod],
  });

  const [selectedInmueble, setSelectedInmueble] = useState<Inmueble | null>(null);
  const [selectedInquilino, setSelectedInquilino] = useState<Cliente | null>(null);
  const [selectedGarantia, setSelectedGarantia] = useState<Garantia | null>(null);
  const [preloadedInmuebleId, setPreloadedInmuebleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [irpfConfig, setIrpfConfig] = useState<(IrpfConfig & { irpf_porcentaje?: string }) | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      if (isEdit) {
        setLoading(true);
        try {
          const [res, configRes]: any = await Promise.all([contractService.getContractById(Number(id)), configService.getAll()]);
          setIrpfConfig(Object.fromEntries((configRes.data?.items ?? []).map((item: { clave: string; valor: string }) => [item.clave, item.valor])));
          const contract = res.data;
          const periodos = Array.isArray(contract.periodos) && contract.periodos.length > 0
            ? contract.periodos
            : [{
                fecha_inicio: contract.fecha_inicio,
                fecha_fin: contract.fecha_fin,
                orden: 1,
                es_prorroga: false,
              }];

          setForm({ ...contract, periodos });
          if (contract.inmueble) setSelectedInmueble(contract.inmueble);
          if (contract.inquilino) setSelectedInquilino(contract.inquilino);
          if (contract.garantia_id && contract.garantia_nombre) {
            setSelectedGarantia({
              id: contract.garantia_id,
              nombre: contract.garantia_nombre,
              tipo: contract.garantia_tipo || 'INSTITUCIONAL',
              descripcion: contract.garantia_descripcion || null,
              es_predefinida: !!contract.garantia_es_predefinida,
            });
          }
        } catch (e: any) {
          setError(e?.response?.data?.message || 'Error al cargar el contrato');
        } finally {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const [numberRes, configRes]: any = await Promise.all([
          contractService.getNextContractNumber(),
          configService.getAll(),
        ]);
        const nextNumber = numberRes.data?.numero_contrato || numberRes.numero_contrato;
        const configItems = configRes.data?.items || [];
        setIrpfConfig(Object.fromEntries(configItems.map((item: { clave: string; valor: string }) => [item.clave, item.valor])));
        const defaultOwnerCommission = configItems.find((item: any) => item.clave === 'default_comision_propietario')?.valor;
        const fallbackCommission = configItems.find((item: any) => item.clave === 'default_commission')?.valor;
        const defaultIva = configItems.find((item: any) => item.clave === 'default_iva')?.valor;

        setForm((prev) => ({
          ...prev,
          numero_contrato: nextNumber,
          comision: parseFloat(defaultOwnerCommission ?? fallbackCommission ?? prev.comision?.toString() ?? '0') || 0,
          iva: parseFloat(defaultIva ?? prev.iva?.toString() ?? '0') || 0,
        }));
      } catch (e) {
        console.error('Error loading next contract defaults:', e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, isEdit]);

  useEffect(() => {
    const inmuebleIdParam = searchParams.get('inmueble_id');
    if (!inmuebleIdParam || isEdit) return;

    const inmuebleId = parseInt(inmuebleIdParam, 10);
    if (Number.isNaN(inmuebleId)) return;

    setPreloadedInmuebleId(inmuebleId);
    inmuebleService.getInmuebleById(inmuebleId).then((res: any) => {
      const inmueble = res.data || res;
      if (!inmueble) return;
      setSelectedInmueble(inmueble);
      setForm((prev) => ({ ...prev, inmueble_id: inmueble.id }));
    }).catch((loadError) => {
      console.error('Error loading preselected inmueble:', loadError);
    });
  }, [searchParams, isEdit]);

  const onChange = (key: keyof Contrato, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (validationErrors[key]) {
      setValidationErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const setPeriodos = (periodos: ContratoPeriodo[]) => {
    setForm((prev) => ({
      ...prev,
      periodos,
      fecha_inicio: periodos[0]?.fecha_inicio || prev.fecha_inicio,
      fecha_fin: periodos[periodos.length - 1]?.fecha_fin || prev.fecha_fin,
    }));
  };

  const updatePeriodo = (index: number, field: keyof ContratoPeriodo, value: string | boolean | number) => {
    const nextPeriodos = [...(form.periodos || [])];
    nextPeriodos[index] = { ...nextPeriodos[index], [field]: value } as ContratoPeriodo;
    setPeriodos(nextPeriodos);
  };

  const addExtensionPeriodo = () => {
    const currentPeriodos = [...(form.periodos || [])];
    const lastPeriodo = currentPeriodos[currentPeriodos.length - 1];
    const previousEnd = parseLocalDateKey(lastPeriodo?.fecha_fin ?? null);
    if (!previousEnd) return;
    const start = addDays(previousEnd, 1);
    currentPeriodos.push({
      fecha_inicio: formatDateForInput(start),
      fecha_fin: formatDateForInput(addYears(start, 1)),
      orden: currentPeriodos.length + 1,
      es_prorroga: true,
    });
    setPeriodos(currentPeriodos);
  };

  const removeExtensionPeriodo = (index: number) => {
    const nextPeriodos = (form.periodos || [])
      .filter((_, periodoIndex) => periodoIndex !== index)
      .map((periodo, periodoIndex) => ({
        ...periodo,
        orden: periodoIndex + 1,
        es_prorroga: periodoIndex > 0,
      }));
    setPeriodos(nextPeriodos);
  };

  const handleInmuebleSelect = (inmueble: Inmueble | null) => {
    setSelectedInmueble(inmueble);
    onChange('inmueble_id', inmueble?.id || undefined);
  };

  const handleInquilinoSelect = (inquilino: Cliente | null) => {
    setSelectedInquilino(inquilino);
    onChange('inquilino_id', inquilino?.id || undefined);
  };

  const handleStartDateChange = (value: string) => {
    onChange('fecha_inicio', value);
    if (isEdit) return;

    const startDate = parseLocalDateKey(value);
    if (!startDate) return;

    const nextPeriodos = [...(form.periodos || [])];
    const nextInitial = buildInitialPeriod(startDate);
    nextPeriodos[0] = nextInitial;
    setPeriodos(nextPeriodos);
    onChange('fecha_reajuste', formatDateForInput(addYears(startDate, 1)));
  };

  const previewOwners = isEdit ? (form.propietarios ?? []) : (selectedInmueble?.propietarios ?? []);
  let ownerPreview: number | null = null;
  let previewError = irpfConfig ? '' : 'Cargando configuración de IRPF…';
  if (irpfConfig && previewOwners.length > 0 && Number(form.monto_alquiler_inquilino) > 0) {
    let net = 0;
    const rate = Number(irpfConfig.irpf_porcentaje ?? 10.5);
    for (const owner of previewOwners) {
      const rent = Math.round(Number(form.monto_alquiler_inquilino) * (owner.porcentaje_propiedad ?? 100)) / 100;
      const retains = selectedInquilino?.agente_retencion_irpf || ownerIrpfStatus(owner, rent, form.moneda_inquilino ?? 'UYU', irpfConfig);
      if (retains === null) {
        previewError = `Configure el mínimo imponible de IRPF en ${form.moneda_inquilino ?? 'UYU'} para calcular el neto.`;
        break;
      }
      const retention = retains ? Math.round(rent * rate) / 100 : 0;
      const fee = owner.no_comision ? 0 : Math.round(rent * Number(form.comision ?? 0)) / 100;
      const vat = Math.round(fee * Number(form.iva ?? 22)) / 100;
      net += rent - retention - fee - vat;
    }
    if (!previewError) ownerPreview = roundByCurrency(net, form.moneda_propietario ?? 'UYU');
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.inmueble_id) errors.inmueble_id = 'Debe seleccionar un inmueble';
    if (!form.inquilino_id) errors.inquilino_id = 'Debe seleccionar un inquilino';
    if (!form.fecha_inicio) errors.fecha_inicio = 'La fecha de inicio es requerida';
    if (!form.fecha_fin) errors.fecha_fin = 'La fecha de fin es requerida';
    if (form.fecha_inicio && form.fecha_fin && form.fecha_inicio >= form.fecha_fin) {
      errors.fecha_fin = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }
    if (!form.monto_alquiler_inquilino || form.monto_alquiler_inquilino <= 0) {
      errors.monto_alquiler_inquilino = 'El monto del alquiler del inquilino es requerido';
    }
    if (ownerPreview === null || ownerPreview <= 0) {
      errors.monto_alquiler_propietario = previewError || 'Seleccione un inmueble con propietarios para calcular el neto';
    }
    if (!form.dia_pago || form.dia_pago < 1 || form.dia_pago > 31) {
      errors.dia_pago = 'El día de pago debe estar entre 1 y 31';
    }
    if (!form.periodos || form.periodos.length === 0) {
      errors.periodos = 'Debe definir al menos un período';
    }
    if (selectedGarantia?.tipo === 'PERSONAL' && !form.garante_nombre) {
      errors.garante_nombre = 'Debe ingresar el nombre del garante';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async () => {
    setError('');
    setMessage('');

    if (!validateForm()) {
      setError('Por favor corrija los errores del formulario');
      return;
    }

    const payload = {
      ...form,
      monto_alquiler_propietario: ownerPreview,
      tipo_reajuste: 'TOTAL',
      tipo_alquiler: form.tipo_alquiler || 'ASEGURADO',
      fecha_inicio: form.periodos?.[0]?.fecha_inicio || form.fecha_inicio,
      fecha_fin: form.periodos?.[form.periodos.length - 1]?.fecha_fin || form.fecha_fin,
      periodos: (form.periodos || []).map((periodo, index) => ({
        ...periodo,
        orden: index + 1,
        es_prorroga: index > 0,
      })),
    };

    setLoading(true);
    try {
      if (isEdit) {
        await contractService.updateContract(Number(id), payload as any);
        setMessage('Contrato actualizado exitosamente');
        setTimeout(() => navigate(`/contracts/${id}`), 1000);
      } else {
        const res = await contractService.createContract(payload as any);
        const newId = (res as any).data?.id || (res as any).id;
        setMessage('Contrato creado exitosamente');
        setTimeout(() => navigate(`/contracts/${newId}`), 1000);
      }
    } catch (submitError: any) {
      console.error('Error saving contract:', submitError);
      setError(submitError?.response?.data?.message || 'Error al guardar el contrato. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Link to="/contracts" className="hover:text-gray-900">Contratos</Link>
            <span>/</span>
            <span className="text-gray-900">{isEdit ? 'Editar Contrato' : 'Nuevo Contrato'}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{isEdit ? 'Editar Contrato' : 'Nuevo Contrato'}</h1>
        </div>

        {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>}
        {message && <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{message}</div>}

        {loading && !form.numero_contrato ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
              <p className="text-gray-600">Cargando...</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">Partes del Contrato</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inmueble <span className="text-red-500">*</span></label>
                  <InmuebleSearchCard
                    selectedInmueble={selectedInmueble}
                    onInmuebleSelect={handleInmuebleSelect}
                    filterEstado={isEdit ? undefined : InmuebleEstado.LIBRE}
                    disabled={preloadedInmuebleId !== null}
                  />
                  {validationErrors.inmueble_id && <p className="text-red-500 text-xs mt-1">{validationErrors.inmueble_id}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inquilino <span className="text-red-500">*</span></label>
                  <ClientSearchCard
                    tipo={TipoCliente.INQUILINO}
                    selectedClient={selectedInquilino}
                    onClientSelect={handleInquilinoSelect}
                  />
                  {validationErrors.inquilino_id && <p className="text-red-500 text-xs mt-1">{validationErrors.inquilino_id}</p>}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Inquilinos Auxiliares</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.inquilinos_auxiliares || ''}
                  onChange={(e) => onChange('inquilinos_auxiliares', e.target.value)}
                  placeholder="Ej: Porto Seguro, u otros nombres adicionales para recibos"
                />
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">Información General</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Contrato</label>
                  <input
                    type="text"
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 ${isEdit ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-blue-500'}`}
                    value={form.numero_contrato || ''}
                    readOnly={isEdit}
                    onChange={isEdit ? undefined : (e) => onChange('numero_contrato', e.target.value)}
                  />
                  {!isEdit && <p className="text-xs text-gray-500 mt-1">Pre-llenado automáticamente; puede personalizarse antes de crear el contrato.</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Inicio <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.fecha_inicio ? 'border-red-500' : 'border-gray-300'}`}
                    value={form.fecha_inicio || ''}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                  />
                  {validationErrors.fecha_inicio && <p className="text-red-500 text-xs mt-1">{validationErrors.fecha_inicio}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Fin del Último Período <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    className={`w-full border rounded-lg px-3 py-2 bg-gray-50 ${validationErrors.fecha_fin ? 'border-red-500' : 'border-gray-300'}`}
                    value={form.fecha_fin || ''}
                    readOnly
                  />
                  {validationErrors.fecha_fin && <p className="text-red-500 text-xs mt-1">{validationErrors.fecha_fin}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.estado || 'ACTIVO'}
                    onChange={(e) => onChange('estado', e.target.value)}
                  >
                    <option value="BORRADOR">Borrador</option>
                    <option value="ACTIVO">Activo</option>
                    <option value="FINALIZADO">Finalizado</option>
                    <option value="RESCINDIDO">Rescindido</option>
                  </select>
                </div>

                <div className="flex items-center pt-6">
                  <input
                    id="auto_renovacion"
                    type="checkbox"
                    checked={!!form.auto_renovacion}
                    onChange={(e) => onChange('auto_renovacion', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="auto_renovacion" className="ml-2 text-sm font-medium text-gray-700">Auto Renovación</label>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-800">Períodos del Contrato</h3>
                  <Button type="button" variant="secondary" onClick={addExtensionPeriodo}>Agregar Prórroga</Button>
                </div>
                <div className="space-y-3">
                  {(form.periodos || []).map((periodo, index) => (
                    <div key={`${periodo.orden}-${index}`} className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded-lg border border-gray-200 p-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{index === 0 ? 'Período Inicial' : `Prórroga ${index}`}</label>
                        <input
                          type="date"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          value={periodo.fecha_inicio}
                          onChange={(e) => updatePeriodo(index, 'fecha_inicio', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Fin</label>
                        <input
                          type="date"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          value={periodo.fecha_fin}
                          onChange={(e) => updatePeriodo(index, 'fecha_fin', e.target.value)}
                        />
                      </div>
                      <div className="flex items-center text-sm text-gray-600">{periodo.es_prorroga ? 'Prórroga anual' : 'Vigencia inicial'}</div>
                      <div className="flex items-center justify-end">
                        {index > 0 && <Button type="button" variant="secondary" onClick={() => removeExtensionPeriodo(index)}>Quitar</Button>}
                      </div>
                    </div>
                  ))}
                </div>
                {validationErrors.periodos && <p className="text-red-500 text-xs mt-2">{validationErrors.periodos}</p>}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">Montos y Pagos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alquiler Inquilino <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.monto_alquiler_inquilino ? 'border-red-500' : 'border-gray-300'}`}
                    value={form.monto_alquiler_inquilino || ''}
                    onChange={(e) => onChange('monto_alquiler_inquilino', parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Moneda Inquilino</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2" value={form.moneda_inquilino || 'UYU'} onChange={(e) => onChange('moneda_inquilino', e.target.value)}>
                    <option value="UYU">UYU (Pesos)</option>
                    <option value="USD">USD (Dólares)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alquiler Propietario <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.monto_alquiler_propietario ? 'border-red-500' : 'border-gray-300'}`}
                    aria-label="Alquiler neto del propietario calculado"
                    value={ownerPreview ?? ''}
                    readOnly
                  />
                  <p className="text-xs text-gray-600 mt-1">{previewError || 'Calculado por propietario con comisión, IVA y retención de IRPF.'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Moneda Propietario</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2" value={form.moneda_propietario || 'UYU'} onChange={(e) => onChange('moneda_propietario', e.target.value)}>
                    <option value="UYU">UYU (Pesos)</option>
                    <option value="USD">USD (Dólares)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comisión (%)</label>
                  <input type="number" step="0.01" className="w-full border border-gray-300 rounded-lg px-3 py-2" value={form.comision || 0} onChange={(e) => onChange('comision', parseFloat(e.target.value) || 0)} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IVA sobre comisión (%)</label>
                  <input type="number" step="0.01" className="w-full border border-gray-300 rounded-lg px-3 py-2" value={form.iva || 0} onChange={(e) => onChange('iva', parseFloat(e.target.value) || 0)} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Día de Pago <span className="text-red-500">*</span></label>
                  <input type="number" min="1" max="31" className={`w-full border rounded-lg px-3 py-2 ${validationErrors.dia_pago ? 'border-red-500' : 'border-gray-300'}`} value={form.dia_pago || 1} onChange={(e) => onChange('dia_pago', parseInt(e.target.value) || 1)} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pago</label>
                  <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-600" value="Mes Vencido" readOnly />
                  <p className="text-xs text-gray-500 mt-1">Todos los contratos utilizan pago a mes vencido.</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
                <div className="md:col-span-2 text-sm text-gray-600">
                  <p>Agente de retención de IRPF: {!selectedInquilino ? 'Seleccione un inquilino' : selectedInquilino.agente_retencion_irpf ? 'Sí, retiene el inquilino' : 'No retiene el inquilino'}</p>
                  <p>Esta condición se configura en la ficha del inquilino. El resguardo se registra en cada recibo.</p>
                </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">Reajustes y Configuración</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Reajuste</label>
                  <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2" value={form.fecha_reajuste || ''} onChange={(e) => onChange('fecha_reajuste', e.target.value || null)} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Alquiler</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2" value={form.tipo_alquiler || 'ASEGURADO'} onChange={(e) => onChange('tipo_alquiler', e.target.value)}>
                    <option value="ASEGURADO">Asegurado</option>
                    <option value="NO_ASEGURADO">No Asegurado</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">Garantía y Convenio</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <GarantiaSelector
                    value={form.garantia_id ? Number(form.garantia_id) : null}
                    onChange={(garantia) => {
                      setSelectedGarantia(garantia);
                      onChange('garantia_id', garantia?.id ?? null);
                      onChange('garantia', garantia?.nombre ?? null);
                      if (garantia?.tipo !== 'PERSONAL') {
                        onChange('garante_nombre', null);
                        onChange('garantia_detalle', null);
                      }
                    }}
                    helperText="Seleccione una garantía normalizada. Las garantías personales requieren el nombre del garante."
                    error={validationErrors.garantia_id}
                  />
                </div>

                {selectedGarantia?.tipo === 'PERSONAL' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Garante <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        className={`w-full border rounded-lg px-3 py-2 ${validationErrors.garante_nombre ? 'border-red-500' : 'border-gray-300'}`}
                        value={form.garante_nombre || ''}
                        onChange={(e) => onChange('garante_nombre', e.target.value)}
                      />
                      {validationErrors.garante_nombre && <p className="text-red-500 text-xs mt-1">{validationErrors.garante_nombre}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Detalle de la Garantía</label>
                      <textarea
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        rows={3}
                        value={form.garantia_detalle || ''}
                        onChange={(e) => onChange('garantia_detalle', e.target.value)}
                      />
                    </div>
                  </>
                )}

                <div className="flex items-center pt-2">
                  <input
                    id="en_convenio"
                    type="checkbox"
                    checked={!!form.en_convenio}
                    onChange={(e) => {
                      onChange('en_convenio', e.target.checked);
                      if (!e.target.checked) onChange('fecha_convenio', null);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="en_convenio" className="ml-2 text-sm font-medium text-gray-700">En convenio de pago</label>
                </div>

                {Boolean(form.en_convenio) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del Convenio</label>
                    <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2" value={form.fecha_convenio || ''} onChange={(e) => onChange('fecha_convenio', e.target.value || null)} />
                  </div>
                )}

                {Boolean(form.en_convenio) && (
                  <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    Los contratos marcados en convenio no aparecerán en el reporte de pagos atrasados; se controlan en el reporte separado de convenios.
                  </div>
                )}
              </div>
            </div>

            <div className="mb-8">

              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">Notas Adicionales</h2>
              <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={4} value={form.notas || ''} onChange={(e) => onChange('notas', e.target.value)} />
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="secondary" onClick={() => navigate('/contracts')}>Cancelar</Button>
              <Button onClick={onSubmit} disabled={loading}>{loading ? 'Guardando...' : isEdit ? 'Actualizar Contrato' : 'Crear Contrato'}</Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
